import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import styled from 'styled-components';
import config from '../../config';
import { getIdToken } from '../../utils/tokenUtils';
import { GET_CONNECTIONS } from '../../GraphQL/settings/queries';
import { DISCONNECT_ATS } from '../../GraphQL/settings/mutations';
import terms from '../../assets/role_relay_terms.pdf';

const ProviderCard = styled(Card)`
  margin-bottom: 1.5rem;
  border: 1px solid #dee2e6;
`;

const ProviderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ProviderLogo = styled.div<{ color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${(props) => props.color};
`;

const ConnectionInfo = styled.div`
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
  margin-top: 1rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #dee2e6;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-weight: 500;
  color: #6c757d;
`;

const InfoValue = styled.span`
  color: #212529;
`;

const HelpSection = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background-color: #e7f3ff;
  border-radius: 0.375rem;
  border-left: 4px solid #0d6efd;
`;

interface Connection {
  id: string;
  type: string;
  status: number;
  createdAt: string;
}

interface ConnectionData {
  connections: {
    items: Connection[];
  };
}

const connectToJobAdder = async () => {
  const token = getIdToken();
  if (!token) {
    throw new Error('No authentication token available');
  }
  const headers = new Headers();
  headers.set('Authorization', `Bearer ${token}`);

  try {
    const response = await fetch(config.jobAdder.integrationUrl, { headers });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('JobAdder connection error:', response.status, errorText);
      throw new Error(`Failed to connect to JobAdder: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (!data.url) {
      console.error('Invalid response from JobAdder API:', data);
      throw new Error('Invalid response from JobAdder API - missing URL');
    }

    window.location.href = data.url;
  } catch (error) {
    console.error('JobAdder connection error:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    alert(`Failed to connect to JobAdder: ${errorMessage}`);
  }
};

const connectToBullhorn = async () => {
  const token = getIdToken();
  if (!token) {
    throw new Error('No authentication token available');
  }
  const headers = new Headers();
  headers.set('Authorization', `Bearer ${token}`);

  try {
    const response = await fetch(config.bullhorn.integrationUrl, { headers });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Bullhorn connection error:', response.status, errorText);
      throw new Error(`Failed to connect to Bullhorn: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (!data.url) {
      console.error('Invalid response from Bullhorn API:', data);
      throw new Error('Invalid response from Bullhorn API - missing URL');
    }

    window.location.href = data.url;
  } catch (error) {
    console.error('Bullhorn connection error:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    alert(`Failed to connect to Bullhorn: ${errorMessage}`);
  }
};

const ATSSettings = () => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [disconnectAts, disconnectAtsResponse] = useMutation(DISCONNECT_ATS);
  const { loading, error, data, refetch } = useQuery<ConnectionData>(GET_CONNECTIONS);

  useEffect(() => {
    if (
      disconnectAtsResponse.called &&
      !disconnectAtsResponse.loading &&
      disconnectAtsResponse?.data?.disconnect
    ) {
      setSuccessMessage('ATS connection disconnected successfully');
      setShowSuccess(true);
      refetch();
      setTimeout(() => setShowSuccess(false), 5000);
    }
  }, [disconnectAtsResponse, refetch]);

  const handleDisconnect = (connectionType: string) => {
    if (
      window.confirm(
        `Are you sure you want to disconnect from ${connectionType}? This will stop syncing roles and candidates.`
      )
    ) {
      disconnectAts({
        variables: {
          connectionType,
        },
      });
    }
  };

  const handleConnect = (provider: 'Bullhorn' | 'JobAdder') => {
    if (provider === 'Bullhorn') {
      connectToBullhorn();
    } else if (provider === 'JobAdder') {
      connectToJobAdder();
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>Loading connections...</CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert variant="destructive">Error loading connections: {error.message}</Alert>
        </CardContent>
      </Card>
    );
  }

  const connections = data?.connections?.items || [];
  const bullhornConnection = connections.find((c) => c.type.toLowerCase() === 'bullhorn');
  const jobAdderConnection = connections.find((c) => c.type.toLowerCase() === 'jobadder');

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: number) => {
    if (status === 1) {
      return <Badge variant="default">Active</Badge>;
    } else if (status === 2) {
      return <Badge variant="destructive">Error</Badge>;
    } else {
      return <Badge variant="secondary">Disconnected</Badge>;
    }
  };

  return (
    <div>
      <h3 style={{ marginBottom: '1.5rem' }}>ATS Connections</h3>

      {showSuccess && (
        <Alert className="mb-4">
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      <p style={{ marginBottom: '2rem', color: '#6c757d' }}>
        Connect your Applicant Tracking System (ATS) to automatically sync roles and candidates.
        You can connect to multiple ATS platforms simultaneously.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          {/* Bullhorn Card */}
          <ProviderCard>
            <CardContent>
              <ProviderHeader>
                <ProviderLogo color="#0066cc">
                  <span>📊</span> Bullhorn
                </ProviderLogo>
                {bullhornConnection && getStatusBadge(bullhornConnection.status)}
              </ProviderHeader>

              {bullhornConnection && bullhornConnection.status === 1 ? (
                <>
                  <p style={{ marginBottom: '1rem', color: '#6c757d' }}>
                    Your Bullhorn account is connected and syncing.
                  </p>

                  <ConnectionInfo>
                    <InfoRow>
                      <InfoLabel>Connection ID:</InfoLabel>
                      <InfoValue>{bullhornConnection.id}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Connected Since:</InfoLabel>
                      <InfoValue>{formatDate(bullhornConnection.createdAt)}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Status:</InfoLabel>
                      <InfoValue>Active & Syncing</InfoValue>
                    </InfoRow>
                  </ConnectionInfo>

                  <div style={{ marginTop: '1.5rem' }}>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDisconnect('Bullhorn')}
                    >
                      Disconnect Bullhorn
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ marginBottom: '1rem', color: '#6c757d' }}>
                    Connect your Bullhorn account to automatically import roles and candidates.
                  </p>
                  <Button variant="default" onClick={() => handleConnect('Bullhorn')}>
                    Connect Bullhorn
                  </Button>
                </>
              )}
            </CardContent>
          </ProviderCard>
        </div>

        <div>
          {/* JobAdder Card */}
          <ProviderCard>
            <CardContent>
              <ProviderHeader>
                <ProviderLogo color="#00a651">
                  <span>🟢</span> JobAdder
                </ProviderLogo>
                {jobAdderConnection && getStatusBadge(jobAdderConnection.status)}
              </ProviderHeader>

              {jobAdderConnection && jobAdderConnection.status === 1 ? (
                <>
                  <p style={{ marginBottom: '1rem', color: '#6c757d' }}>
                    Your JobAdder account is connected and syncing.
                  </p>

                  <ConnectionInfo>
                    <InfoRow>
                      <InfoLabel>Connection ID:</InfoLabel>
                      <InfoValue>{jobAdderConnection.id}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Connected Since:</InfoLabel>
                      <InfoValue>{formatDate(jobAdderConnection.createdAt)}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Status:</InfoLabel>
                      <InfoValue>Active & Syncing</InfoValue>
                    </InfoRow>
                  </ConnectionInfo>

                  <div style={{ marginTop: '1.5rem' }}>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDisconnect('JobAdder')}
                    >
                      Disconnect JobAdder
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ marginBottom: '1rem', color: '#6c757d' }}>
                    Connect your JobAdder account to automatically import roles and candidates.
                  </p>
                  <Button variant="default" onClick={() => handleConnect('JobAdder')}>
                    Connect JobAdder
                  </Button>
                </>
              )}
            </CardContent>
          </ProviderCard>
        </div>
      </div>

      {/* Help Section */}
      <HelpSection>
        <h5 style={{ marginBottom: '1rem' }}>📘 About ATS Connections</h5>
        <ul style={{ marginBottom: 0, paddingLeft: '1.5rem' }}>
          <li>You can connect to multiple ATS platforms simultaneously</li>
          <li>Roles and candidates will sync automatically once connected</li>
          <li>
            By connecting your ATS, you confirm you have read and agree with our{' '}
            <a href={terms} target="_blank" rel="noreferrer">
              terms and conditions
            </a>
          </li>
          <li>Disconnecting will stop syncing but won't delete existing data</li>
        </ul>
      </HelpSection>
    </div>
  );
};

export default ATSSettings;
